import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Loader2,
  Maximize2,
  AlertTriangle,
} from 'lucide-react';
import { api, KinveraApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { COMMON_PRINT_SIZES, type PrintSizeId } from '../services/resolution';

type PhotoVersion = {
  id: string;
  type: string;
  label: string;
  imageUrl: string | null;
  width: number | null;
  height: number | null;
  megapixels: number | null;
  createdAt: string | null;
  isCurrent: boolean;
  factor: number | null;
  purpose: string | null;
  targetPrintSize: string | null;
  effectivePpi: number | null;
  requiresCrop: boolean | null;
  provider: string | null;
  model: string | null;
  upscaleRequired: boolean | null;
};

type PrintPlan = {
  targetPrintSize: string;
  targetPrintLabel: string;
  currentEffectivePpi: number;
  qualityRating: string;
  requiresCrop: boolean;
  upscaleRequired: boolean;
  recommendedFactor: number | null;
  projectedEffectivePpi: number | null;
  canPrepare: boolean;
  message: string;
  printWidthIn: number;
  printHeightIn: number;
};

function formatDate(iso?: string | null) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function extForUrl(url: string | null | undefined) {
  if (!url) return 'jpg';
  if (url.includes('image/png') || url.includes('.png')) return 'png';
  if (url.includes('image/webp') || url.includes('.webp')) return 'webp';
  return 'jpg';
}

export const PhotoDetailPage: React.FC = () => {
  const { jobId = '' } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState('Photo');
  const [versions, setVersions] = useState<PhotoVersion[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [printPlans, setPrintPlans] = useState<PrintPlan[]>([]);
  const [walgreensMessage, setWalgreensMessage] = useState(
    'Walgreens photo ordering will be available here soon.'
  );
  const [showPrepare, setShowPrepare] = useState(false);
  const [preparingSize, setPreparingSize] = useState<string | null>(null);
  const [prepareMessage, setPrepareMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selected = versions.find((v) => v.id === selectedId) || versions[0] || null;

  const load = useCallback(async () => {
    if (!jobId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const [detail, printCfg] = await Promise.all([
        api.photoDetail(jobId, token),
        api.printConfig().catch(() => null),
      ]);
      setFileName(detail.fileName || 'Photo');
      setVersions((detail.versions || []) as PhotoVersion[]);
      setSelectedId(detail.displayAssetId || detail.versions?.[0]?.id || null);
      setPrintPlans((detail.printPlans || []) as PrintPlan[]);
      if (printCfg?.providers?.[0]?.message) {
        setWalgreensMessage(printCfg.providers[0].message);
      }
    } catch (err) {
      setError(
        err instanceof KinveraApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not load photo'
      );
    } finally {
      setLoading(false);
    }
  }, [jobId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectVersion = async (id: string) => {
    setSelectedId(id);
    if (!jobId || !token) return;
    try {
      await api.setDisplayAsset(jobId, id, token);
      setVersions((prev) => prev.map((v) => ({ ...v, isCurrent: v.id === id })));
      // Refresh print plans for newly selected dimensions via photo-detail
      const detail = await api.photoDetail(jobId, token);
      setPrintPlans((detail.printPlans || []) as PrintPlan[]);
      // Keep selection; server may have recomputed display
      if (detail.versions) {
        setVersions(detail.versions as PhotoVersion[]);
      }
    } catch {
      /* selection still works locally */
    }
  };

  const downloadSelected = () => {
    if (!selected?.imageUrl) return;
    const a = document.createElement('a');
    a.href = selected.imageUrl;
    const base = fileName.replace(/\.[^/.]+$/, '') || 'photo';
    let suffix = 'photo';
    if (selected.type === 'print-ready' && selected.targetPrintSize) {
      suffix = `print-${selected.targetPrintSize}`;
    } else if (selected.type === 'upscaled' && selected.factor) {
      suffix = `upscaled-${selected.factor}x`;
    } else if (selected.type === 'restored') {
      suffix = 'restored';
    } else if (selected.type === 'original') {
      suffix = 'original';
    }
    a.download = `kinvera-${base}-${suffix}.${extForUrl(selected.imageUrl)}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const prepareFor = async (sizeId: PrintSizeId) => {
    if (!jobId || !token || !selected) return;
    setPreparingSize(sizeId);
    setPrepareMessage(null);
    setBusy(true);
    try {
      const result = await api.preparePrint(
        jobId,
        { sourceAssetId: selected.id, targetPrintSize: sizeId },
        token
      );

      if (result.pending && result.operationId) {
        setPrepareMessage(result.message || 'Preparing…');
        const started = Date.now();
        while (Date.now() - started < 10 * 60 * 1000) {
          await new Promise((r) => setTimeout(r, 2000));
          const op = await api.upscaleOperation(result.operationId!, token);
          if (op.status === 'completed') {
            setPrepareMessage('Print preparation complete.');
            await load();
            setShowPrepare(false);
            return;
          }
          if (op.status === 'failed') {
            throw new KinveraApiError(op.error?.message || 'Print preparation failed.', {
              code: op.error?.code || 'UPSCALE_FAILED',
            });
          }
        }
        throw new KinveraApiError('Preparation is taking longer than expected. Check Library shortly.', {
          code: 'UPSCALE_TIMEOUT',
          retryable: true,
        });
      }

      setPrepareMessage(result.message || 'Marked print-ready.');
      await load();
      setShowPrepare(false);
    } catch (err) {
      setPrepareMessage(
        err instanceof KinveraApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Print preparation failed'
      );
    } finally {
      setBusy(false);
      setPreparingSize(null);
    }
  };

  if (loading) {
    return (
      <main className="kin-page">
        <p className="kin-upscale-loading">
          <Loader2 size={18} className="animate-spin" /> Loading photo…
        </p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="kin-page">
        <p className="kin-banner kin-banner-error">{error}</p>
        <Link to="/app/library" className="kin-btn kin-btn-ghost">
          Back to Library
        </Link>
      </main>
    );
  }

  return (
    <main className="kin-page kin-photo-detail-page">
      <div className="kin-page-head">
        <div>
          <button
            type="button"
            className="kin-btn kin-btn-ghost"
            onClick={() => navigate('/app/library')}
          >
            <ArrowLeft size={16} aria-hidden /> Library
          </button>
          <h1 style={{ marginTop: '0.75rem' }}>{fileName}</h1>
        </div>
        <div className="kin-library-head-actions">
          <button
            type="button"
            className="kin-btn kin-btn-primary"
            onClick={() => setShowPrepare(true)}
          >
            <Maximize2 size={16} aria-hidden /> Prepare for Print
          </button>
        </div>
      </div>

      <div className="kin-photo-detail-layout">
        <div className="kin-photo-detail-preview">
          {selected?.imageUrl ? (
            <img src={selected.imageUrl} alt={selected.label} />
          ) : (
            <p>No preview available for this version.</p>
          )}
        </div>

        <aside className="kin-photo-detail-sidebar">
          <section>
            <h2>Versions</h2>
            <ul className="kin-photo-version-list">
              {versions.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    className={`kin-photo-version-btn${selectedId === v.id ? ' is-active' : ''}`}
                    onClick={() => void selectVersion(v.id)}
                  >
                    <strong>{v.label}</strong>
                    {v.width && v.height ? (
                      <span>
                        {v.width} × {v.height}
                        {v.megapixels != null ? ` · ${v.megapixels} MP` : ''}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {selected ? (
            <section>
              <h2>Selected version</h2>
              <p>
                <strong>{selected.label}</strong>
              </p>
              {selected.width && selected.height ? (
                <p>
                  {selected.width.toLocaleString()} × {selected.height.toLocaleString()}
                  {selected.megapixels != null ? ` · ${selected.megapixels} MP` : ''}
                </p>
              ) : null}
              {selected.createdAt ? <p>{formatDate(selected.createdAt)}</p> : null}
              {selected.provider ? (
                <p className="kin-muted">
                  {selected.provider}
                  {selected.model ? ` · ${selected.model}` : ''}
                  {selected.factor ? ` · ${selected.factor}×` : ''}
                </p>
              ) : null}
              {selected.type === 'print-ready' ? (
                <p className="kin-banner kin-banner-info">
                  This version is prepared for printing
                  {selected.targetPrintSize ? ` (${selected.targetPrintSize})` : ''}.
                </p>
              ) : null}
            </section>
          ) : null}

          <section>
            <h2>Print quality</h2>
            {printPlans.length === 0 ? (
              <p>Select a version with known dimensions to see print guidance.</p>
            ) : (
              <ul className="kin-print-plan-list">
                {printPlans.map((p) => (
                  <li key={p.targetPrintSize}>
                    <strong>{p.targetPrintLabel}</strong>
                    <span>
                      {Math.round(p.currentEffectivePpi)} PPI — {p.qualityRating}
                    </span>
                    {p.requiresCrop ? <em>Crop will be required</em> : null}
                    {p.upscaleRequired && p.recommendedFactor ? (
                      <em>Recommend {p.recommendedFactor}× for ~{Math.round(p.projectedEffectivePpi || 0)} PPI</em>
                    ) : !p.upscaleRequired ? (
                      <em>No enhancement needed</em>
                    ) : (
                      <em>{p.message}</em>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2>Actions</h2>
            <div className="kin-library-card-actions" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                className="kin-btn kin-btn-ghost"
                disabled={!selected?.imageUrl}
                onClick={downloadSelected}
              >
                <Download size={16} aria-hidden /> Download
              </button>
              <button
                type="button"
                className="kin-btn kin-btn-primary"
                onClick={() => setShowPrepare(true)}
              >
                Prepare for Print
              </button>
            </div>
          </section>

          {selected?.type === 'print-ready' ? (
            <section className="kin-print-ready-next">
              <h2>Ready to print</h2>
              <p>{walgreensMessage}</p>
              <p className="kin-muted">Download remains available for manual inspection.</p>
            </section>
          ) : null}
        </aside>
      </div>

      {showPrepare ? (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
            aria-label="Close prepare panel"
            disabled={busy}
            onClick={() => !busy && setShowPrepare(false)}
          />
          <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900">Prepare for Print</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Kinvera chooses the smallest enhancement needed for ~300 PPI. Source: {selected?.label}
              </p>
            </div>
            <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
              {(printPlans.length
                ? printPlans
                : COMMON_PRINT_SIZES.map((s) => ({
                    targetPrintSize: s.id,
                    targetPrintLabel: s.label,
                    currentEffectivePpi: 0,
                    qualityRating: 'Unknown',
                    requiresCrop: false,
                    upscaleRequired: true,
                    recommendedFactor: null,
                    projectedEffectivePpi: null,
                    canPrepare: false,
                    message: 'Dimensions unavailable',
                    printWidthIn: s.widthIn,
                    printHeightIn: s.heightIn,
                  }))
              ).map((p) => (
                <div
                  key={p.targetPrintSize}
                  className="rounded-xl border border-slate-200 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <strong className="text-slate-900">{p.targetPrintLabel}</strong>
                      <p className="text-xs text-slate-600 mt-1">
                        Current: {Math.round(p.currentEffectivePpi)} PPI — {p.qualityRating}
                      </p>
                      {p.requiresCrop ? (
                        <p className="text-xs text-amber-700 mt-1">Crop will be required to fill this size.</p>
                      ) : null}
                      <p className="text-xs text-slate-500 mt-1">{p.message}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="kin-btn kin-btn-primary mt-3"
                    disabled={busy || !p.canPrepare}
                    onClick={() => void prepareFor(p.targetPrintSize as PrintSizeId)}
                  >
                    {preparingSize === p.targetPrintSize ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Working…
                      </>
                    ) : p.upscaleRequired ? (
                      `Prepare for ${p.targetPrintLabel}`
                    ) : (
                      `Continue for ${p.targetPrintLabel}`
                    )}
                  </button>
                  {!p.canPrepare ? (
                    <p className="text-xs text-rose-600 mt-2 flex items-start gap-1">
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                      Choose a smaller print size for the best result.
                    </p>
                  ) : null}
                </div>
              ))}
              {prepareMessage ? (
                <p className="text-sm text-slate-700">{prepareMessage}</p>
              ) : null}
            </div>
            <div className="px-4 py-3 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                className="kin-btn kin-btn-ghost"
                disabled={busy}
                onClick={() => setShowPrepare(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};
